import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { HxIcon } from '../../icon/icon';
import { HxTableCellDirective } from '../directives/table-cell';
import { HxTableRowDirective } from '../directives/table-row';

/**
 * Expandable Row Component (hx-expandable-row)
 *
 * A row component that can expand to show additional content (like a sub-table). Follows HxTable's granular building
 * block pattern with content projection.
 *
 * @example
 *   ```html
 *   <hx-table>
 *     <hx-table-header>
 *       <hx-header-cell>Name</hx-header-cell>
 *       <hx-header-cell>Status</hx-header-cell>
 *     </hx-table-header>
 *
 *     <hx-expandable-row *ngFor="let user of users">
 *       <hx-table-cell>{{user.name}}</hx-table-cell>
 *       <hx-table-cell>{{user.status}}</hx-table-cell>
 *
 *       <ng-container expandableContent>
 *         <hx-table>
 *           <hx-table-row *ngFor="let detail of user.details">
 *             <hx-table-cell>{{detail.info}}</hx-table-cell>
 *           </hx-table-row>
 *         </hx-table>
 *       </ng-container>
 *     </hx-expandable-row>
 *   </hx-table>
 *   ```;
 */
@Component({
  selector: 'hx-expandable-row',
  standalone: true,
  imports: [HxIcon, HxTableRowDirective, HxTableCellDirective],
  templateUrl: './expandable-row.html',
  styleUrl: './expandable-row.css',
  exportAs: 'hxExpandableRow',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-expandable-row',
    '[class.hx-expandable-row--expanded]': 'expanded()',
  },
})
export class HxExpandableRowComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  /** Whether row is expanded (can be controlled from parent) */
  readonly expanded = input<boolean>(false);

  /** Internal expansion state signal */
  private readonly _expanded = signal<boolean>(false);

  /** Whether row is selected */
  readonly selected = input<boolean>(false);

  /** Custom background color for main row */
  readonly backgroundColor = input<string>();

  /** Custom text color for main row */
  readonly color = input<string>();

  /** Custom border color */
  readonly borderColor = input<string>();

  /** Emits when expansion state changes */
  readonly expandedChange = output<boolean>();

  constructor() {
    // Sync input expanded state with internal signal
    effect(() => {
      this._expanded.set(this.expanded());
    });
  }

  /** Toggle expansion state */
  toggleExpansion(): void {
    const newState = !this.expanded();
    this._expanded.set(newState);
    this.expandedChange.emit(newState);
  }

  /** Expand the row */
  expand(): void {
    if (!this.expanded()) {
      this._expanded.set(true);
      this.expandedChange.emit(true);
    }
  }

  /** Collapse the row */
  collapse(): void {
    if (this.expanded()) {
      this._expanded.set(false);
      this.expandedChange.emit(false);
    }
  }
}
